import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { Plus, Search } from "lucide-react";
import { useState } from "react";
import { useUrlFilters } from "../../shared/hooks/urlFilters";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../app/AuthContext";
import { api } from "../../shared/api/client";
import {
  Button,
  DataTable,
  ErrorState,
  Field,
  Input,
  FilterBar,
  PageHeader,
  Select,
  StatusBadge,
  type Column,
} from "../../shared/ui";
import { PinButton, PinnedProjectsBar, projectDragProps } from "./projectPins";
import type { PageResponse, ProjectStatus, ProjectSummary } from "../../shared/types/api";
import { ProjectFormDrawer } from "./ProjectFormDrawer";
import {
  HEALTH_TONE,
  STATUS_LABELS,
  STATUS_TONE,
  formatHours,
  formatVariance,
} from "./projectVocabulary";

const STATUS_OPTIONS: ProjectStatus[] = [
  "DRAFT",
  "PLANNING",
  "PENDING_APPROVAL",
  "APPROVED",
  "ACTIVE",
  "ON_HOLD",
  "COMPLETED",
  "CANCELLED",
  "ARCHIVED",
];

/**
 * The portfolio list (§99).
 *
 * <p>Every row shows the plan against what was committed, because that comparison is the reason
 * the record exists. A project with no approved baseline says so plainly rather than showing a
 * zero, which would read as "nothing agreed" when it means "nothing agreed yet".
 */
/* The parameters this screen owns; outside the component so useUrlFilters can memoise on it. */
const PROJECT_FILTERS = { q: "", status: "", page: "0", size: "25" };

export function ProjectsPage() {
  const navigate = useNavigate();
  const { can } = useAuth();
  /* In the URL: opening a project and coming back returns to this list as it was left. */
  const [filters, setFilters] = useUrlFilters(PROJECT_FILTERS);
  const page = Number(filters.page) || 0;
  const size = Number(filters.size) || 25;
  const { q: query } = filters;
  const status = filters.status as ProjectStatus | "";
  const setPage = (next: number) => setFilters({ page: String(next) });
  const setSize = (next: number) => setFilters({ size: String(next), page: "0" });
  const [creating, setCreating] = useState(false);

  const projects = useQuery({
    queryKey: ["projects", page, size, query, status],
    queryFn: () => {
      const search = new URLSearchParams({
        page: String(page),
        size: String(size),
        sort: "name,asc",
      });
      if (query) {
        search.set("q", query);
      }
      if (status) {
        search.set("status", status);
      }
      return api.get<PageResponse<ProjectSummary>>(`/api/v1/projects?${search}`);
    },
    placeholderData: keepPreviousData,
  });

  const columns: Column<ProjectSummary>[] = [
    {
      /* First, where the eye already is when scanning keys, and narrow enough not to cost the
         name any width. */
      key: "pin",
      header: "",
      className: "w-10",
      render: (project) => <PinButton projectId={project.id} name={project.name} />,
    },
    {
      key: "projectKey",
      header: "Key",
      render: (project) => (
        <span className="font-mono text-xs text-ink-500">{project.projectKey}</span>
      ),
    },
    {
      key: "name",
      header: "Name",
      render: (project) => <span className="font-medium text-ink-900">{project.name}</span>,
    },
    {
      key: "status",
      header: "Status",
      render: (project) => (
        <StatusBadge tone={STATUS_TONE[project.status]}>
          {STATUS_LABELS[project.status]}
        </StatusBadge>
      ),
    },
    {
      key: "health",
      header: "Health",
      render: (project) => (
        <StatusBadge tone={HEALTH_TONE[project.health]}>{project.health}</StatusBadge>
      ),
    },
    {
      key: "projectManagerName",
      header: "Manager",
      render: (project) => project.projectManagerName ?? "—",
    },
    {
      key: "baseline",
      header: "Committed",
      className: "text-right tnum",
      render: (project) =>
        project.hasBaseline ? (
          formatHours(project.baselineMinutes / 60)
        ) : (
          // Not zero: nothing has been agreed yet, which is a different statement.
          <span className="text-ink-400">no baseline</span>
        ),
    },
    {
      key: "planned",
      header: "Planned",
      className: "text-right tnum",
      render: (project) => formatHours(project.approvedMinutes / 60),
    },
    {
      key: "variance",
      header: "Variance",
      className: "text-right tnum",
      render: (project) => {
        if (!project.hasBaseline) {
          return <span className="text-ink-400">—</span>;
        }
        const varianceHours = (project.approvedMinutes - project.baselineMinutes) / 60;
        if (varianceHours === 0) {
          return <span className="text-ink-400">on budget</span>;
        }
        return (
          <span className={varianceHours > 0 ? "font-medium text-red-600" : "text-emerald-600"}>
            {formatVariance(varianceHours)}
          </span>
        );
      },
    },
    {
      key: "plannedEndDate",
      header: "Finish",
      className: "tnum",
    },
  ];

  return (
    <>
      <PageHeader
        title="Projects"
        subtitle="What was committed, what is planned now, and the gap between them."
      />

      <FilterBar
        actions={
          can("PROJECT_CREATE") && (
            <Button icon={<Plus className="h-4 w-4" />} onClick={() => setCreating(true)}>
              New project
            </Button>
          )
        }
      >
          <div className="w-72">
            <Field label="Search">
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-400" />
                <Input
                  className="pl-9"
                  placeholder="Name or key"
                  value={query}
                  onChange={(event) => {
                    setFilters({ q: event.target.value, page: "0" });
                  }}
                />
              </div>
            </Field>
          </div>
          <div className="w-56">
            <Field label="Status">
              <Select
                value={status}
                onChange={(event) => {
                  setFilters({ status: event.target.value, page: "0" });
                }}
              >
                <option value="">All statuses</option>
                {STATUS_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {STATUS_LABELS[option]}
                  </option>
                ))}
              </Select>
            </Field>
          </div>
      </FilterBar>

      {/* Under the filters rather than inside them: it is not a filter, it is a shortcut, and it
          grows a line as projects are added to it. */}
      <div className="mb-4">
        <PinnedProjectsBar />
      </div>

      {projects.isError ? (
        <ErrorState error={projects.error} onRetry={() => void projects.refetch()} />
      ) : (
        <DataTable
          columns={columns}
          rows={projects.data?.content ?? []}
          rowKey={(project) => project.id}
          loading={projects.isPending}
          rowProps={(project) => projectDragProps(project.id)}
          onRowClick={(project) => navigate(`/projects/${project.id}`)}
          page={page}
          pageSize={size}
          totalElements={projects.data?.totalElements ?? 0}
          onPageChange={setPage}
          onPageSizeChange={setSize}
          emptyTitle={query || status ? "Nothing matches those filters" : "No projects yet"}
          emptyMessage={
            query || status
              ? "Try a broader search, or clear the status filter."
              : "A project records what was committed and what is planned. Create one to start tracking the difference."
          }
          emptyAction={
            can("PROJECT_CREATE") &&
            !query &&
            !status && (
              <Button icon={<Plus className="h-4 w-4" />} onClick={() => setCreating(true)}>
                Create the first project
              </Button>
            )
          }
        />
      )}

      <ProjectFormDrawer
        open={creating}
        onClose={() => setCreating(false)}
        onSaved={(project) => {
          void projects.refetch();
          navigate(`/projects/${project.id}`);
        }}
      />
    </>
  );
}
